import {
  Activity,
  ArrowRightLeft,
  BookOpen,
  Building2,
  Calculator,
  CandlestickChart,
  ChartPie,
  Cloud,
  Coins,
  Compass,
  Database,
  GitPullRequest,
  KeyRound,
  LayoutDashboard,
  Map,
  MonitorPlay,
  Network,
  NotebookPen,
  NotebookTabs,
  ScanSearch,
  Search,
  Shield,
  ShieldCheck,
  Split,
  Target,
  UserRound,
  Waypoints,
  Workflow,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type * as PageTree from "fumadocs-core/page-tree";
import type { ReactNode } from "react";

/** All icons available for use in docs pages and sidebar */
export const docIcons = {
  Activity,
  ArrowRightLeft,
  BookOpen,
  Building2,
  Calculator,
  CandlestickChart,
  ChartPie,
  Cloud,
  Coins,
  Compass,
  Database,
  GitPullRequest,
  KeyRound,
  LayoutDashboard,
  Map,
  MonitorPlay,
  Network,
  NotebookPen,
  NotebookTabs,
  ScanSearch,
  Search,
  Shield,
  ShieldCheck,
  Split,
  Target,
  UserRound,
  Waypoints,
  Workflow,
  Wrench,
} as const satisfies Record<string, LucideIcon>;

/** Icon name derived from the registry */
export type DocIconName = keyof typeof docIcons;

// ── Sidebar icon configuration ───────────────────────────────────────

/** Maps slug prefixes to icon names. Single-segment = top section, multi-segment = first layer. */
const sidebarIcons: Record<string, DocIconName> = {
  learn: "Compass",
  column: "NotebookPen",
  reference: "NotebookTabs",
  "learn/chapter-1": "Waypoints",
  "learn/chapter-1/financial-products": "LayoutDashboard",
  "learn/chapter-2": "CandlestickChart",
  "learn/chapter-3": "Split",
  "learn/chapter-4": "ChartPie",
  "reference/glossary": "ScanSearch",
  "reference/formulas": "Calculator",
  "reference/faq": "NotebookTabs",
};

const nonClickableSections = new Set(["learn", "column", "reference"]);

/** Resolve page header icon from sidebar mappings (longest prefix match → fallback) */
export function getDocPageIconName(slug?: string[]): DocIconName {
  const parts = slug ?? [];
  for (let i = parts.length; i > 0; i--) {
    const icon = sidebarIcons[parts.slice(0, i).join("/")];
    if (icon) return icon;
  }
  return "BookOpen";
}

// ── Sidebar tree decoration ─────────────────────────────────────────

function toSlug(url: string): string[] {
  const normalized = url
    .replace(/^https?:\/\/[^/]+/, "")
    .replace(/^(?:\/(ja|en))?(?:\/docs)?(?=\/|$)/, "")
    .replace(/^\/+|\/+$/g, "");
  return normalized ? normalized.split("/") : [];
}

/** Find the sidebar icon key for a folder node by examining its first descendant page URL */
function nodeIconKey(node: PageTree.Node, depth: number): string | null {
  const findUrl = (n: PageTree.Node): string | undefined => {
    if (n.type === "separator") return undefined;
    if (n.type === "page") return n.url;
    if (n.index) return n.index.url;
    for (const child of n.children) {
      const url = findUrl(child);
      if (url) return url;
    }
    return undefined;
  };

  const url = findUrl(node);
  if (!url) return null;
  const slug = toSlug(url);
  return slug.length > depth ? slug.slice(0, depth + 1).join("/") : null;
}

function iconBadge(Icon: LucideIcon, className?: string, key?: string): ReactNode {
  return (
    <span key={key} className={className ? `velpha-sidebar-icon ${className}` : "velpha-sidebar-icon"} aria-hidden>
      <Icon className="size-3.5" />
    </span>
  );
}

function decorateNode(node: PageTree.Node, depth = 0): PageTree.Node {
  if (node.type === "separator") return node;

  if (node.type === "page") {
    const slug = toSlug(node.url).join("/");
    if (depth === 0 && slug.length === 0) {
      return { ...node, icon: node.icon ?? iconBadge(docIcons.BookOpen, undefined, "sidebar-home-icon") };
    }
    if (depth === 1) {
      const iconName = sidebarIcons[slug];
      if (iconName) {
        return { ...node, icon: node.icon ?? iconBadge(docIcons[iconName], undefined, `sidebar-page-icon-${slug}`) };
      }
    }
    return node;
  }

  const section = nodeIconKey(node, depth);
  const sectionIconName = section ? sidebarIcons[section] : undefined;
  const matchesIndexName =
    node.index && typeof node.name === "string" && typeof node.index.name === "string" && node.name === node.index.name;
  const hideIndexLink = matchesIndexName || (depth === 0 && section !== null && nonClickableSections.has(section));

  return {
    ...node,
    index: hideIndexLink ? undefined : node.index,
    name: hideIndexLink ? (
      <span key={`sidebar-section-title-${section}`} className="velpha-sidebar-section-title text-primary">
        {node.name}
      </span>
    ) : (
      node.name
    ),
    icon: sectionIconName
      ? iconBadge(
          docIcons[sectionIconName],
          hideIndexLink ? "velpha-sidebar-section-icon text-primary" : undefined,
          `sidebar-section-icon-${section}`
        )
      : node.icon,
    defaultOpen: depth === 0,
    collapsible: true,
    children: node.children.map((child) => decorateNode(child, depth + 1)),
  };
}

export function withSidebarTreeIcons(tree: PageTree.Root, rootPage?: { name: ReactNode; url: string }): PageTree.Root {
  let prepared = tree;
  if (rootPage && !tree.children.some((c) => c.type === "page" && c.url === rootPage.url)) {
    prepared = { ...tree, children: [{ type: "page", name: rootPage.name, url: rootPage.url }, ...tree.children] };
  }

  return {
    ...prepared,
    children: prepared.children.map((child) => decorateNode(child)),
    fallback: prepared.fallback ? withSidebarTreeIcons(prepared.fallback, rootPage) : undefined,
  };
}
